import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import Loader from "react-loader-spinner";
import JSZip from "jszip";
import { FiArrowLeft, FiDownload, FiAlertCircle, FiSquare, FiCheckSquare } from "react-icons/fi";
import { API_URLS, SOCIAL_EVENT_TYPES, SOCIAL_MVP_LABELS } from '../constants/config';
import {
    fetchSocialPatterns,
    fetchPictureBase64,
    fetchAdminLeaguesWithSeasons,
    fetchLeagueStages,
    fetchStageMatches,
    fetchMatchesByDate,
    fetchLeagueGroups,
} from '../actions/socialPicturesActions';
import {
    normalizePattern,
    isPatternAvailable,
    patternNeedsManualPick,
    patternNeedsCustomImage,
    patternHasEventTypes,
    patternHasGroups,
    patternIsPerMatch,
    safeFileName,
    downloadBlob,
    resolveImage,
} from '../utilities/infographics';
import '../styles/editModal.css';
import '../styles/infographics.css';

// Пакетная выгрузка картинок.
// Два режима:
//   tour — лига + сезон + тур: картинки на каждый матч тура плюс те,
//          что рисуются на тур целиком (таблица, топ-5, сборная);
//   day  — дата: только картинки на матч, по всем лигам админа за этот день.
// Пачкой не выгружаются шаблоны со своим фоном (фон подбирают вручную)
// и те, где админ сам собирает состав: сборная тура, MVP, тренеры, плей-офф.
// Исключение — топ-5: там фон тоже выбирается, но по умолчанию подставляются
// фото игроков из базы. Топ-5 разворачивается по типам статистики,
// таблицы по группам — по группам сезона.
//
// Картинки рисует сервер, и это тяжёлая операция, поэтому запросы идут
// строго по одному с прогрессом и возможностью остановиться.

export default function InfographicsBatch() {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const userState = useSelector(state => state.user);
    const clientId = userState && userState.userData && userState.userData.data
        ? userState.userData.data.client_id
        : null;

    const modeParam = new URLSearchParams(location.search).get('mode');
    const initialMode = ['day', 'leagues'].indexOf(modeParam) !== -1 ? modeParam : 'tour';
    const [mode, setMode] = useState(initialMode);
    // Режим leagues: одни и те же шаблоны на тур целиком, но сразу
    // по нескольким лигам клиента. Матчи тут не участвуют
    const [chosenLeagueIds, setChosenLeagueIds] = useState([]);

    const [patterns, setPatterns] = useState([]);
    const [patternsLoading, setPatternsLoading] = useState(true);

    const [leagues, setLeagues] = useState([]);
    const [league, setLeague] = useState(null);
    const [seasonIndex, setSeasonIndex] = useState(0);
    const [leaguePickerOpen, setLeaguePickerOpen] = useState(false);

    const [stages, setStages] = useState([]);
    const [stage, setStage] = useState(null);
    const [date, setDate] = useState('');

    const [matches, setMatches] = useState([]);
    const [matchesLoading, setMatchesLoading] = useState(false);
    const [excludedMatches, setExcludedMatches] = useState([]);
    const [excludedPatterns, setExcludedPatterns] = useState([]);

    const [groups, setGroups] = useState([]);
    // Типы статистики для топ-5: по умолчанию берём все
    const [excludedEvents, setExcludedEvents] = useState([]);

    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0, current: '' });
    const [error, setError] = useState(null);
    const [failures, setFailures] = useState([]);
    const stopRef = useRef(false);

    // ---- Справочники ----
    useEffect(() => {
        let cancelled = false;
        setPatternsLoading(true);
        dispatch(fetchSocialPatterns(0, 0, 500))
            .then(data => {
                if (cancelled) return;
                setPatterns((Array.isArray(data) ? data : []).map(normalizePattern));
            })
            .catch(e => { if (!cancelled) setError(e); })
            .finally(() => { if (!cancelled) setPatternsLoading(false); });
        return () => { cancelled = true; };
    }, [dispatch]);

    useEffect(() => {
        let cancelled = false;
        dispatch(fetchAdminLeaguesWithSeasons())
            .then(data => {
                if (cancelled) return;
                const rows = (Array.isArray(data) ? data : (data && data.data) || [])
                    .filter(item => Number(item.league_id) > 0);
                setLeagues(rows);
            })
            .catch(e => { if (!cancelled) console.error('Ошибка загрузки лиг:', e); });
        return () => { cancelled = true; };
    }, [dispatch]);

    const activeSeason = useMemo(() => {
        if (!league || !league.seasons || !league.seasons.length) return null;
        return league.seasons[seasonIndex] || league.seasons[0];
    }, [league, seasonIndex]);

    useEffect(() => {
        if (!league || !activeSeason) { setStages([]); return undefined; }
        let cancelled = false;
        dispatch(fetchLeagueStages(league.league_id, activeSeason.season_id))
            .then(data => {
                if (cancelled) return;
                setStages(Array.isArray(data) ? data : (data && data.data) || []);
            })
            .catch(e => { if (!cancelled) console.error('Ошибка загрузки туров:', e); });
        return () => { cancelled = true; };
    }, [dispatch, league, activeSeason]);

    // ---- Группы сезона ----
    // Нужны, чтобы развернуть таблицы по группам: без группы сервер такую
    // картинку не собирает и запрос повисает
    useEffect(() => {
        if (mode !== 'tour' || !league || !activeSeason) { setGroups([]); return undefined; }
        let cancelled = false;
        dispatch(fetchLeagueGroups(league.league_id, activeSeason.season_id, false))
            .then(data => {
                if (cancelled) return;
                setGroups(Array.isArray(data) ? data : (data && data.data) || []);
            })
            .catch(e => { if (!cancelled) { console.error('Ошибка загрузки групп:', e); setGroups([]); } });
        return () => { cancelled = true; };
    }, [dispatch, mode, league, activeSeason]);

    // ---- Матчи ----
    const loadMatches = useCallback(() => {
        setExcludedMatches([]);
        if (mode === 'tour') {
            if (!league || !activeSeason || !stage) { setMatches([]); return; }
            setMatchesLoading(true);
            dispatch(fetchStageMatches(league.league_id, activeSeason.season_id, stage.id))
                .then(data => setMatches(Array.isArray(data) ? data : (data && data.data) || []))
                .catch(e => { console.error('Ошибка загрузки матчей:', e); setMatches([]); })
                .finally(() => setMatchesLoading(false));
            return;
        }
        if (!date) { setMatches([]); return; }
        setMatchesLoading(true);
        dispatch(fetchMatchesByDate(date, clientId))
            .then(data => setMatches(Array.isArray(data) ? data : (data && data.data) || []))
            .catch(e => { console.error('Ошибка загрузки матчей:', e); setMatches([]); })
            .finally(() => setMatchesLoading(false));
    }, [dispatch, mode, league, activeSeason, stage, date, clientId]);

    useEffect(() => { loadMatches(); }, [loadMatches]);

    // ---- Что можно выгрузить ----
    const available = useMemo(() => {
        const mine = patterns.filter(one => isPatternAvailable(one, clientId));
        // Прячем и то, где нужен свой фон, и то, где состав собирают руками.
        // Топ-5 остаётся: там фон по умолчанию — фото игроков из базы
        const withPhoto = mine.filter(patternNeedsCustomImage);
        const manual = mine.filter(one => !patternNeedsCustomImage(one)
            && patternNeedsManualPick(one));
        const batchable = mine.filter(one => !patternNeedsCustomImage(one)
            && !patternNeedsManualPick(one));
        return {
            perMatch: mode === 'leagues' ? [] : batchable.filter(patternIsPerMatch),
            // На тур целиком — только в режиме тура: без лиги их не построить
            perTour: mode === 'day' ? [] : batchable.filter(one => !patternIsPerMatch(one)),
            photoCount: withPhoto.length,
            manualCount: manual.length,
        };
    }, [patterns, clientId, mode]);

    // Активные лиги клиента: enabled и с назначенным активным сезоном.
    // По ним и выгружаем таблицы, топ-5 и расписание
    const activeLeagues = useMemo(() => leagues.filter(one => (
        Number(one.enabled) !== 0 && Number(one.league_id) > 0
    )), [leagues]);

    const isLeagueOn = (id) => chosenLeagueIds.indexOf(Number(id)) !== -1;

    const toggleLeague = (id) => setChosenLeagueIds(prev => (
        prev.indexOf(Number(id)) === -1
            ? prev.concat([Number(id)])
            : prev.filter(one => one !== Number(id))
    ));

    // По умолчанию отмечаем все активные лиги, как и шаблоны
    useEffect(() => {
        if (mode !== 'leagues') return;
        setChosenLeagueIds(activeLeagues.map(one => Number(one.league_id)));
    }, [mode, activeLeagues]);

    const seasonOf = (one) => (
        one.seasons && one.seasons.length ? one.seasons[0] : null
    );

    const isPatternOn = (id) => excludedPatterns.indexOf(id) === -1;
    const isMatchOn = (id) => excludedMatches.indexOf(id) === -1;

    const togglePattern = (id) => setExcludedPatterns(prev => (
        prev.indexOf(id) === -1 ? prev.concat([id]) : prev.filter(one => one !== id)
    ));

    const toggleMatch = (id) => setExcludedMatches(prev => (
        prev.indexOf(id) === -1 ? prev.concat([id]) : prev.filter(one => one !== id)
    ));

    const chosenMatches = matches.filter(one => isMatchOn(one.id));
    const chosenPerMatch = available.perMatch.filter(one => isPatternOn(one.id));
    const chosenPerTour = available.perTour.filter(one => isPatternOn(one.id));

    const chosenEvents = SOCIAL_EVENT_TYPES.filter(
        one => excludedEvents.indexOf(one.value) === -1,
    );
    const toggleEvent = (value) => setExcludedEvents(prev => (
        prev.indexOf(value) === -1 ? prev.concat([value]) : prev.filter(one => one !== value)
    ));

    // Секция типов статистики нужна, только если среди выбранных шаблонов
    // есть топ-5
    const eventsMatter = chosenPerTour.some(patternHasEventTypes)
        || chosenPerMatch.some(patternHasEventTypes);

    // Один шаблон может дать несколько картинок: топ-5 — по картинке
    // на каждый тип статистики, таблица по группам — на каждую группу
    const variantsOf = (pattern) => {
        let variants = [{ suffix: '', params: {} }];
        if (patternHasEventTypes(pattern)) {
            variants = chosenEvents.map(one => ({
                suffix: one.label,
                params: { event_type: one.value },
            }));
        }
        if (patternHasGroups(pattern)) {
            const byGroup = [];
            groups.forEach(group => {
                variants.forEach(variant => {
                    byGroup.push({
                        suffix: variant.suffix
                            ? `${variant.suffix} — ${group.name}`
                            : `Группа ${group.name}`,
                        params: { ...variant.params, groups: [group.name] },
                    });
                });
            });
            variants = byGroup;
        }
        return variants;
    };

    const chosenLeagues = activeLeagues.filter(one => isLeagueOn(one.league_id));

    const perTourImages = mode === 'leagues'
        ? chosenLeagues.length * chosenPerTour.reduce(
            (sum, pattern) => sum + variantsOf(pattern).length, 0,
        )
        : chosenPerTour.reduce(
            (sum, pattern) => sum + variantsOf(pattern).length, 0,
        );
    const perMatchImages = chosenMatches.length * chosenPerMatch.reduce(
        (sum, pattern) => sum + variantsOf(pattern).length, 0,
    );
    const totalImages = perTourImages + perMatchImages;

    // ---- Выгрузка ----
    const requestUrlFor = (pattern) => (
        pattern.route && pattern.route.length
            ? pattern.route
            : (API_URLS[pattern.url] || pattern.url)
    );

    // Значения по умолчанию — те же, с которыми открывается редактор
    // и приложение. Пропущенное поле сервер трактует иначе, чем false,
    // поэтому отправляем полный набор, а не только заполненное
    const defaultParams = () => ({
        date,
        partnersEnable: false,
        vk: '',
        inst: '',
        label: '',
        groups: false,
        event_type: SOCIAL_EVENT_TYPES[0].value,
        mvpLabel_type: SOCIAL_MVP_LABELS[0].label,
        goalTimeEnabled: true,
        isGroupEvent: false,
        videoEnabled: false,
        includeTourTeam: true,
        activeColor: 1,
        activeHeader: 1,
        activeBottom: 1,
    });

    // Матч из matchesByDate приходит со своей лигой — в режиме дня берём её,
    // иначе картинка соберётся не по тому турниру
    const paramsForMatch = (match) => ({
        ...defaultParams(),
        league_id: match.league_id || (league ? league.league_id : undefined),
        season_id: match.season_id || (activeSeason ? activeSeason.season_id : undefined),
        stage_id: match.stage_id || (stage ? stage.id : undefined),
        match_id: match.id,
    });

    const paramsForTour = () => ({
        ...defaultParams(),
        league_id: league ? league.league_id : undefined,
        season_id: activeSeason ? activeSeason.season_id : undefined,
        stage_id: stage ? stage.id : undefined,
    });

    const run = async () => {
        if (totalImages === 0) return;
        stopRef.current = false;
        setRunning(true);
        setError(null);
        setFailures([]);
        const problems = [];
        const zip = new JSZip();
        let done = 0;
        setProgress({ done: 0, total: totalImages, current: '' });

        // Задания собираем заранее, чтобы прогресс был честным
        const jobs = [];

        // Режим «по лигам»: те же шаблоны на тур целиком, но для каждой
        // отмеченной лиги в её активном сезоне
        if (mode === 'leagues') {
            chosenLeagues.forEach(one => {
                const season = seasonOf(one);
                const folderLeague = safeFileName(one.league_name, 'Лига');
                chosenPerTour.forEach(pattern => {
                    const folder = safeFileName(pattern.title, 'Картинка');
                    variantsOf(pattern).forEach(variant => {
                        jobs.push({
                            pattern,
                            params: {
                                ...defaultParams(),
                                league_id: one.league_id,
                                season_id: season ? season.season_id : undefined,
                                ...variant.params,
                            },
                            path: variant.suffix
                                ? `${folderLeague}/${folder}/${safeFileName(variant.suffix, 'Вариант')}.png`
                                : `${folderLeague}/${folder}.png`,
                            label: `${one.league_name} — ${pattern.title}`
                                + (variant.suffix ? ` — ${variant.suffix}` : ''),
                        });
                    });
                });
            });
        }

        if (mode !== 'leagues') chosenPerTour.forEach(pattern => {
            const folder = safeFileName(pattern.title, 'Картинка');
            variantsOf(pattern).forEach(variant => {
                jobs.push({
                    pattern,
                    params: { ...paramsForTour(), ...variant.params },
                    path: variant.suffix
                        ? `${folder}/${safeFileName(variant.suffix, 'Вариант')}.png`
                        : `${folder}.png`,
                    label: variant.suffix
                        ? `${pattern.title} — ${variant.suffix}`
                        : pattern.title,
                });
            });
        });
        chosenMatches.forEach(match => {
            const matchName = `${match.home_team_name || 'Команда'} - ${match.guest_team_name || 'Команда'}`;
            chosenPerMatch.forEach(pattern => {
                const folder = safeFileName(pattern.title, 'Картинка');
                variantsOf(pattern).forEach(variant => {
                    const file = variant.suffix
                        ? `${safeFileName(matchName, 'Матч')} — ${safeFileName(variant.suffix, 'Вариант')}`
                        : safeFileName(matchName, 'Матч');
                    jobs.push({
                        pattern,
                        params: { ...paramsForMatch(match), ...variant.params },
                        path: `${folder}/${file}.png`,
                        label: `${pattern.title} — ${file}`,
                    });
                });
            });
        });

        // Строго по одному запросу: сервер рисует каждую картинку
        // headless-браузером, залп его положит
        for (let i = 0; i < jobs.length; i++) {
            if (stopRef.current) break;
            const job = jobs[i];
            setProgress({ done, total: jobs.length, current: job.label });
            try {
                const data = await dispatch(fetchPictureBase64(
                    requestUrlFor(job.pattern), job.params,
                ));
                if (data && typeof data === 'string') {
                    // Две команды с одинаковыми названиями дали бы один путь,
                    // и вторая картинка молча затёрла бы первую
                    let path = job.path;
                    let copy = 2;
                    while (zip.file(path)) {
                        path = job.path.replace(/\.png$/, ` (${copy}).png`);
                        copy += 1;
                    }
                    zip.file(path, data, { base64: true });
                } else {
                    problems.push(job.label);
                }
            } catch (e) {
                console.error('Не удалось получить картинку:', job.label, e);
                problems.push(job.label);
            }
            done += 1;
            setProgress({ done, total: jobs.length, current: job.label });
        }

        try {
            const files = Object.keys(zip.files).length;
            if (files === 0) {
                setError(new Error('Ни одной картинки получить не удалось'));
            } else {
                const blob = await zip.generateAsync({ type: 'blob' });
                const base = mode === 'tour'
                    ? `${safeFileName(league && league.league_name, 'Лига')} ${safeFileName(stage && stage.name, 'тур')}`
                    : `Матчи ${date}`;
                downloadBlob(blob, `${safeFileName(base, 'Инфографика')}.zip`);
            }
        } catch (e) {
            console.error('Ошибка сборки архива:', e);
            setError(e);
        }

        setFailures(problems);
        setRunning(false);
    };

    const ready = mode === 'tour'
        ? Boolean(league && activeSeason && stage)
        : mode === 'leagues'
            ? chosenLeagues.length > 0
            : Boolean(date);

    const renderPatternList = (list, title, hint) => {
        if (list.length === 0) return null;
        return (
            <div className="igBatchGroup">
                <h4 className="igSlotSectionTitle">{title}</h4>
                {hint ? <p className="igControlNote">{hint}</p> : null}
                {list.map(pattern => (
                    <button
                        type="button"
                        key={pattern.id}
                        className={`igListRow ${isPatternOn(pattern.id) ? 'igListRowChosen' : ''}`}
                        onClick={() => togglePattern(pattern.id)}
                    >
                        {isPatternOn(pattern.id) ? <FiCheckSquare /> : <FiSquare />}
                        {resolveImage(pattern.image) ? (
                            <img src={resolveImage(pattern.image)} alt="" />
                        ) : null}
                        <span className="igListRowMain">
                            <span className="igListRowTitle">{pattern.title}</span>
                        </span>
                    </button>
                ))}
            </div>
        );
    };

    return (
        <div className="layoutWrapper">
            <div className="matchCenterWrapper">
                <div className="matchCenterHeader">
                    <button type="button" className="backButton" onClick={() => history.push('/infographics')}>
                        <FiArrowLeft /> К шаблонам
                    </button>
                    <span className="matchCenterName">Пакетная выгрузка</span>
                </div>

                <div className="igPage">
                    <div className="igSegmented igBatchModes">
                        <button
                            type="button"
                            className={`igSegmentedItem ${mode === 'tour' ? 'igSegmentedItemActive' : ''}`}
                            onClick={() => setMode('tour')}
                        >
                            Картинки тура
                        </button>
                        <button
                            type="button"
                            className={`igSegmentedItem ${mode === 'day' ? 'igSegmentedItemActive' : ''}`}
                            onClick={() => setMode('day')}
                        >
                            Картинки дня
                        </button>
                        <button
                            type="button"
                            className={`igSegmentedItem ${mode === 'leagues' ? 'igSegmentedItemActive' : ''}`}
                            onClick={() => setMode('leagues')}
                        >
                            По всем лигам
                        </button>
                    </div>

                    <div className="igBatchNote">
                        <FiAlertCircle />
                        <span>
                            {mode === 'tour'
                                ? 'Картинки на каждый матч тура плюс те, что рисуются на тур целиком — таблица, топ-5, сборная.'
                                : mode === 'leagues'
                                    ? 'Таблицы, расписание и топ-5 сразу по нескольким активным лигам, каждая в своём активном сезоне. Картинки на отдельные матчи в этом режиме не выгружаются.'
                                    : 'Картинки на каждый матч выбранной даты, по всем вашим лигам сразу.'}
                            {available.photoCount > 0 ? (
                                <> Пачкой не выгружаются {available.photoCount} шаблонов
                                    со своим фоном — картинку для каждого подбирают вручную.</>
                            ) : null}
                            {available.manualCount > 0 ? (
                                <> Ещё {available.manualCount} пропущено, потому что состав
                                    в них собирает админ: сборная тура, MVP, тренеры, плей-офф.</>
                            ) : null}
                            {' '}Топ-5 выгружается: фон там по умолчанию —
                            фото игроков из базы.
                        </span>
                    </div>

                    <div className="igBatchGrid">
                        <div className="igBatchColumn">
                            <h3 className="igParamsTitle">Что выгружаем</h3>

                            {mode === 'leagues' ? (
                                <div className="igControl">
                                    <div className="igControlHead">
                                        <span className="igControlLabel">Активные лиги</span>
                                        <span className="igControlHint">
                                            Каждая лига берётся в своём активном сезоне
                                        </span>
                                    </div>
                                    {activeLeagues.length === 0 ? (
                                        <p className="igControlNote">Активных лиг не нашлось.</p>
                                    ) : activeLeagues.map(one => (
                                        <button
                                            type="button"
                                            key={one.league_id}
                                            className={`igListRow ${isLeagueOn(one.league_id) ? 'igListRowChosen' : ''}`}
                                            onClick={() => toggleLeague(one.league_id)}
                                        >
                                            {isLeagueOn(one.league_id) ? <FiCheckSquare /> : <FiSquare />}
                                            {resolveImage(one.league_image) ? (
                                                <img src={resolveImage(one.league_image)} alt="" />
                                            ) : null}
                                            <span className="igListRowMain">
                                                <span className="igListRowTitle">{one.league_name}</span>
                                                <span className="igListRowMeta">
                                                    {one.championat_name}
                                                    {seasonOf(one) ? ` · ${seasonOf(one).season_name}` : ''}
                                                </span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            ) : mode === 'tour' ? (
                                <>
                                    <div className="igControl">
                                        <div className="igControlHead">
                                            <span className="igControlLabel">Лига</span>
                                        </div>
                                        <button
                                            type="button"
                                            className="igPicker"
                                            onClick={() => setLeaguePickerOpen(true)}
                                        >
                                            <span className={league ? 'igPickerValue' : 'igPickerPlaceholder'}>
                                                {league ? league.league_name : 'Выберите лигу'}
                                            </span>
                                        </button>
                                    </div>

                                    {league ? (
                                        <div className="igControl">
                                            <div className="igControlHead">
                                                <span className="igControlLabel">Сезон</span>
                                            </div>
                                            <select
                                                className="igSelect"
                                                value={seasonIndex}
                                                onChange={(e) => setSeasonIndex(Number(e.target.value))}
                                            >
                                                {(league.seasons || []).map((season, index) => (
                                                    <option key={season.season_id} value={index}>
                                                        {season.season_name || season.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    ) : null}

                                    <div className="igControl">
                                        <div className="igControlHead">
                                            <span className="igControlLabel">Тур</span>
                                        </div>
                                        <select
                                            className="igSelect"
                                            value={stage ? stage.id : ''}
                                            disabled={stages.length === 0}
                                            onChange={(e) => setStage(
                                                stages.find(one => String(one.id) === e.target.value) || null,
                                            )}
                                        >
                                            <option value="">
                                                {stages.length ? '— выберите тур —' : 'Сначала выберите лигу'}
                                            </option>
                                            {stages.map(one => (
                                                <option key={one.id} value={one.id}>{one.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            ) : (
                                <div className="igControl">
                                    <div className="igControlHead">
                                        <span className="igControlLabel">Дата</span>
                                    </div>
                                    <input
                                        type="date"
                                        className="igInput"
                                        value={date}
                                        onChange={(e) => setDate(e.target.value)}
                                    />
                                </div>
                            )}

                            <div className="igControl" hidden={mode === 'leagues'}>
                                <div className="igControlHead">
                                    <span className="igControlLabel">Матчи</span>
                                    <span className="igControlHint">
                                        Снимите галочку, чтобы пропустить матч
                                    </span>
                                </div>
                                {matchesLoading ? (
                                    <div className="igLoader">
                                        <Loader type="Oval" color="#04a9f5" height={32} width={32} />
                                    </div>
                                ) : !ready ? (
                                    <p className="igControlNote">
                                        {mode === 'tour' ? 'Выберите лигу и тур.' : 'Выберите дату.'}
                                    </p>
                                ) : matches.length === 0 ? (
                                    <p className="igControlNote">Матчей не нашлось.</p>
                                ) : matches.map(match => (
                                    <button
                                        type="button"
                                        key={match.id}
                                        className={`igListRow ${isMatchOn(match.id) ? 'igListRowChosen' : ''}`}
                                        onClick={() => toggleMatch(match.id)}
                                    >
                                        {isMatchOn(match.id) ? <FiCheckSquare /> : <FiSquare />}
                                        <span className="igListRowMain">
                                            <span className="igListRowTitle">
                                                {match.home_team_name} — {match.guest_team_name}
                                            </span>
                                            {match.league_name ? (
                                                <span className="igListRowMeta">{match.league_name}</span>
                                            ) : null}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="igBatchColumn">
                            <h3 className="igParamsTitle">Шаблоны</h3>
                            {patternsLoading ? (
                                <div className="igLoader">
                                    <Loader type="Oval" color="#04a9f5" height={32} width={32} />
                                </div>
                            ) : (
                                <>
                                    {renderPatternList(
                                        available.perMatch,
                                        'На каждый матч',
                                        'Афиши и результаты — по картинке на матч.',
                                    )}
                                    {renderPatternList(
                                        available.perTour,
                                        'На тур целиком',
                                        groups.length > 0
                                            ? `Таблицы и топ-5. Таблицы по группам выгрузятся на каждую из ${groups.length} групп.`
                                            : 'Таблицы и топ-5 — по одной картинке.',
                                    )}
                                    {eventsMatter ? (
                                        <div className="igBatchGroup">
                                            <h4 className="igSlotSectionTitle">Типы статистики</h4>
                                            <p className="igControlNote">
                                                Топ-5 выгружается по картинке на каждый отмеченный тип.
                                            </p>
                                            {SOCIAL_EVENT_TYPES.map(item => (
                                                <button
                                                    type="button"
                                                    key={item.value}
                                                    className={`igListRow ${excludedEvents.indexOf(item.value) === -1 ? 'igListRowChosen' : ''}`}
                                                    onClick={() => toggleEvent(item.value)}
                                                >
                                                    {excludedEvents.indexOf(item.value) === -1
                                                        ? <FiCheckSquare /> : <FiSquare />}
                                                    <span className="igListRowMain">
                                                        <span className="igListRowTitle">{item.label}</span>
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    ) : null}

                                    {available.perMatch.length === 0 && available.perTour.length === 0 ? (
                                        <p className="igControlNote">
                                            Подходящих шаблонов нет: во всех доступных вам
                                            вставляется своё фото.
                                        </p>
                                    ) : null}
                                </>
                            )}
                        </div>
                    </div>

                    <div className="igBatchFooter">
                        <div className="igBatchSummary">
                            <strong>{totalImages}</strong>
                            <span>
                                {' '}картинок в архиве
                                {mode === 'leagues' && chosenLeagues.length > 0 ? (
                                    <> — {chosenLeagues.length} лиг × {chosenPerTour.length} шаблонов</>
                                ) : null}
                                {mode !== 'leagues' && chosenPerMatch.length > 0 && chosenMatches.length > 0 ? (
                                    <> — {chosenMatches.length} матчей × {chosenPerMatch.length} шаблонов
                                        {chosenPerTour.length > 0 ? ` + ${chosenPerTour.length} на тур` : ''}</>
                                ) : null}
                            </span>
                        </div>

                        {running ? (
                            <div className="igBatchProgress">
                                <div className="igBatchProgressBar">
                                    <span style={{
                                        width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%`,
                                    }} />
                                </div>
                                <span className="igControlHint">
                                    Готово {progress.done} из {progress.total}
                                    {progress.current ? ` · ${progress.current}` : ''}
                                </span>
                                <button
                                    type="button"
                                    className="igGhostBtn"
                                    onClick={() => { stopRef.current = true; }}
                                >
                                    Остановить
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="igPrimaryBtn"
                                disabled={totalImages === 0}
                                onClick={run}
                            >
                                <FiDownload /> Скачать архив
                            </button>
                        )}
                    </div>

                    {error ? (
                        <div className="igError">
                            {(error.response && error.response.data) || error.message || 'Не удалось собрать архив'}
                        </div>
                    ) : null}

                    {failures.length > 0 ? (
                        <div className="igBatchNote">
                            <FiAlertCircle />
                            <span>
                                Не получилось собрать {failures.length} картинок, остальные в архиве:
                                {' '}{failures.slice(0, 5).join('; ')}
                                {failures.length > 5 ? ' и другие' : ''}.
                            </span>
                        </div>
                    ) : null}
                </div>
            </div>

            {leaguePickerOpen && (
                <div className="modalOverlay" onClick={() => setLeaguePickerOpen(false)}>
                    <div className="modalContent" onClick={(e) => e.stopPropagation()}>
                        <div className="modalHeader">
                            <h2>Выберите лигу</h2>
                            <button className="modalClose" onClick={() => setLeaguePickerOpen(false)}>×</button>
                        </div>
                        <div className="modalBody modalBodyScroll">
                            {leagues.length === 0 ? (
                                <p className="igControlNote">Лиги не найдены.</p>
                            ) : leagues.map(one => (
                                <button
                                    type="button"
                                    key={one.league_id}
                                    className="igListRow"
                                    onClick={() => {
                                        setLeague(one);
                                        setSeasonIndex(0);
                                        setStage(null);
                                        setLeaguePickerOpen(false);
                                    }}
                                >
                                    {resolveImage(one.league_image) ? (
                                        <img src={resolveImage(one.league_image)} alt="" />
                                    ) : null}
                                    <span className="igListRowMain">
                                        <span className="igListRowTitle">{one.league_name}</span>
                                        <span className="igListRowMeta">{one.championat_name}</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
